/**
 * channels-tab.tsx — Tab "Kênh": tài khoản kênh của tổ chức (nền tảng, trạng thái, chủ kênh,
 * hoạt động gần nhất). Tắt / bật kênh có lý do (cờ isDisabled — cùng cờ trang kênh của khách).
 */
import { useState } from 'react'
import { Power, PowerOff } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { StatCard } from '@/components/shared/stat-card'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { ErrorState } from '@/components/shared/feedback'
import { PlatformTag } from '@/components/shared/platform-mark'
import { platformApiError } from '@/lib/platform-client'
import { useOrgChannels, useSetChannelDisabled, type OrgChannel } from '@/hooks/use-platform-org-manage'
import { ReasonDialog } from './reason-dialog'
import { CHANNEL_STATE, fmtDateTime } from './labels'

const PAGE_SIZE = 50

export function ChannelsTab({ orgId }: { orgId: string }) {
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useOrgChannels(orgId, { includeDeleted, page, limit: PAGE_SIZE })
  const [target, setTarget] = useState<OrgChannel | null>(null)
  const m = useSetChannelDisabled(orgId)

  const columns: Column<OrgChannel>[] = [
    {
      key: 'channel', header: 'Kênh', cell: (c) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{c.displayName || '(không tên)'}</p>
          <div className="mt-0.5 flex items-center gap-1.5">
            <PlatformTag platform={c.platform} />
            {c.phone && <span className="text-xs text-muted-foreground">{c.phone}</span>}
          </div>
        </div>
      ),
    },
    {
      key: 'state', header: 'Trạng thái', cell: (c) => {
        const s = CHANNEL_STATE[c.state]
        return (
          <div className="flex flex-col items-start gap-0.5">
            <Badge variant={s.variant}>{s.text}</Badge>
            {c.lockedByPlatform && <Badge variant="destructive">Bị khoá bởi quản trị hệ thống</Badge>}
            {c.deletedAt && <span className="text-xs text-muted-foreground">{fmtDateTime(c.deletedAt)}</span>}
          </div>
        )
      },
    },
    { key: 'owner', header: 'Chủ kênh', cell: (c) => c.owner ? <span title={c.owner.email}>{c.owner.fullName}</span> : '—' },
    { key: 'created', header: 'Ngày tạo', cell: (c) => fmtDateTime(c.createdAt) },
    { key: 'connected', header: 'Kết nối gần nhất', cell: (c) => fmtDateTime(c.lastConnectedAt) },
    { key: 'activity', header: 'Tin gần nhất', cell: (c) => fmtDateTime(c.lastActivityAt) },
    { key: 'conv', header: 'Hội thoại', align: 'right', cell: (c) => c.conversations },
    {
      key: 'actions', header: '', align: 'right', cell: (c) =>
        c.deletedAt ? null : (
          <Button variant="ghost" size="sm" onClick={() => setTarget(c)}>
            {c.isDisabled ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
            {c.isDisabled ? 'Bật' : 'Tắt'}
          </Button>
        ),
    },
  ]

  return (
    <div className="space-y-4">
      {data && (
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="Đã kết nối" value={data.summary.connected} tone="success" />
          <StatCard label="Mất kết nối" value={data.summary.disconnected} tone="destructive" />
          <StatCard label="Đã tắt" value={data.summary.disabled} tone="warning" />
        </div>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={includeDeleted} onChange={(e) => { setIncludeDeleted(e.target.checked); setPage(1) }} />
        Hiện cả kênh đã xoá
      </label>
      {isError ? <ErrorState message="Không tải được danh sách kênh." /> : (
        <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(c) => c.id} emptyTitle="Tổ chức chưa có kênh" />
      )}
      {data && data.total > PAGE_SIZE && <Pagination page={page} limit={PAGE_SIZE} total={data.total} onPageChange={setPage} />}

      {target && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setTarget(null)}
          title={`${target.isDisabled ? 'Bật' : 'Tắt'} kênh — ${target.displayName || '(không tên)'}`}
          description={target.isDisabled
            ? 'Kênh nhận/gửi tin trở lại (Zalo cá nhân tự kết nối lại nếu còn phiên).'
            : 'Kênh ngừng nhận/gửi tin; Zalo cá nhân bị ngắt kết nối ngay. Dữ liệu hội thoại giữ nguyên.'}
          confirmLabel={target.isDisabled ? 'Bật kênh' : 'Tắt kênh'} destructive={!target.isDisabled} pending={m.isPending}
          onConfirm={async (reason) => {
            try {
              await m.mutateAsync({ channelId: target.id, isDisabled: !target.isDisabled, reason })
              toast.success(target.isDisabled ? 'Đã bật kênh' : 'Đã tắt kênh')
              setTarget(null)
            } catch (err) { toast.error(platformApiError(err)) }
          }}
        />
      )}
    </div>
  )
}
